'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { PROFILE_POSITIONS_QK } from '@/lib/api/history/profile-hooks';
import type { ProfileOpenPosition } from '@/lib/api/history/profile-types';
import { type BalanceUpdatePayload, useBalanceUpdates } from '@/lib/providers/WalletSocketProvider';
import { isPositionBalanceUpdate } from '@/lib/utils/positionBalance';

/**
 * Re-reads the open prediction positions when one opens or closes on the wallet
 * socket: the list is cached for minutes, so a trade on the same page left the count stale.
 */
export function useOpenPositionsCountSync(userId: string, positions?: ProfileOpenPosition[]) {
  const queryClient = useQueryClient();
  const { subscribe } = useBalanceUpdates();

  const handleBalanceUpdate = useCallback(
    ({ assetId, b, aop, c }: BalanceUpdatePayload) => {
      const isHeld = positions?.some((position) => position.assetId === assetId) ?? false;
      const isOpened = !isHeld && b > 0 && isPositionBalanceUpdate({ aop, c });
      const isClosed = isHeld && b === 0;
      if (!isOpened && !isClosed) return;
      queryClient.invalidateQueries({
        queryKey: [PROFILE_POSITIONS_QK, 'PREDICTION', userId, 'OPEN_POSITIONS'],
      });
    },
    [queryClient, userId, positions],
  );

  useEffect(() => {
    if (!userId) return;
    return subscribe(handleBalanceUpdate);
  }, [userId, subscribe, handleBalanceUpdate]);
}
